/// Two-flag (and occasionally three-flag) ICS / IRPCS Volume I
/// signal combinations. Each entry is a hoist that the receiver
/// reads top-to-bottom; the meaning is fixed by the International
/// Code of Signals and a few have specific IRPCS Annex IV
/// references (NC is the classic "vessel in distress" combination).
///
/// Starter set covers the highest-yield distress / safety / harbour
/// signals — enough for the RYA Day Skipper + Yachtmaster syllabus.
/// Expand here as the catalogue matures. We deliberately avoid the
/// many rare or obsolete combinations (mutiny, sabotage, etc.) —
/// they add noise without teaching value.

export interface ComboSpec {
  /// Concatenated flag characters: "NC", "AB", etc. Used as the
  /// stable answer key in the flashcards game.
  id: string;
  /// Flag characters in hoist-order (top to bottom).
  flags: readonly string[];
  /// Full operational meaning.
  meaning: string;
  /// Button-sized label.
  shortMeaning: string;
}

export const ICS_COMBOS: Readonly<Record<string, ComboSpec>> = Object.freeze({
  NC: {
    id: "NC",
    flags: ["N", "C"],
    meaning: "I am in distress and require immediate assistance.",
    shortMeaning: "In distress",
  },
  AB: {
    id: "AB",
    flags: ["A", "B"],
    meaning: "I require a tug urgently.",
    shortMeaning: "Need tug urgently",
  },
  AC: {
    id: "AC",
    flags: ["A", "C"],
    meaning: "I am abandoning my vessel.",
    shortMeaning: "Abandoning vessel",
  },
  AN: {
    id: "AN",
    flags: ["A", "N"],
    meaning: "I need a doctor.",
    shortMeaning: "Need a doctor",
  },
  CB: {
    id: "CB",
    flags: ["C", "B"],
    meaning: "I require immediate assistance.",
    shortMeaning: "Need help now",
  },
  DV: {
    id: "DV",
    flags: ["D", "V"],
    meaning: "I am drifting.",
    shortMeaning: "Drifting",
  },
  IT: {
    id: "IT",
    flags: ["I", "T"],
    meaning: "I am on fire.",
    shortMeaning: "On fire",
  },
  JL: {
    id: "JL",
    flags: ["J", "L"],
    meaning: "You are running the risk of going aground.",
    shortMeaning: "Risk of going aground",
  },
  LO: {
    id: "LO",
    flags: ["L", "O"],
    meaning: "I am not in my correct position (to a vessel marking station).",
    shortMeaning: "Not in correct position",
  },
  NE2: {
    id: "NE2",
    flags: ["N", "E", "2"],
    meaning:
      "Submarines are operating in this vicinity — submerged submarines may be encountered. Proceed with caution.",
    shortMeaning: "Submarines exercising — proceed with caution",
  },
  NF: {
    id: "NF",
    flags: ["N", "F"],
    meaning: "You are running into danger.",
    shortMeaning: "Running into danger",
  },
  PP: {
    id: "PP",
    flags: ["P", "P"],
    meaning: "Keep well clear of me.",
    shortMeaning: "Keep well clear",
  },
  RY: {
    id: "RY",
    flags: ["R", "Y"],
    meaning:
      "You should proceed at slow speed when passing me (or vessels making this signal). Used by survey vessels, divers, dredgers and similar craft for whom wash from passing traffic is hazardous.",
    shortMeaning: "Proceed at slow speed when passing",
  },
  UM: {
    id: "UM",
    flags: ["U", "M"],
    meaning: "The harbour (or port) is closed to traffic.",
    shortMeaning: "Harbour closed",
  },
  YU: {
    id: "YU",
    flags: ["Y", "U"],
    meaning: "I am going to communicate with your station by the International Code of Signals.",
    shortMeaning: "Will signal by ICS",
  },
  YG: {
    id: "YG",
    flags: ["Y", "G"],
    meaning: "You appear not to be complying with the traffic-separation scheme.",
    shortMeaning: "Not complying with TSS",
  },
});

export const COMBO_IDS: readonly string[] = Object.keys(ICS_COMBOS);
